import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import {
  Button,
  Input,
  Label,
  Spinner,
  Table,
  Collapse,
  FormGroup,
} from "reactstrap";
import { Link } from "react-router-dom";
import {
  API_TIMEOUT,
  BELANJA_MAMIN_PD,
  BELANJA_MAMIN_PD_CHECK,
  BELANJA_MAMIN_PD_TAHUN,
  BELANJA_MAMIN_PD_UPLOAD,
  DINAS_PERANGKAT_DAERAH,
} from "../../utils";
import s from "./belanjaMamin.module.scss";

const CSV_COLUMNS = [
  { key: "nama_dinas", label: "Nama Perangkat Daerah", required: true },
  { key: "tahun", label: "Tahun", required: true },
  { key: "total_belanja_mamin", label: "Total Belanja Mamin", required: true },
];

const parseCSVLine = (line, delimiter = ",") => {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') inQuotes = !inQuotes;
    else if (c === delimiter && !inQuotes) {
      result.push(current.trim().replace(/^"|"$/g, ""));
      current = "";
    } else current += c;
  }
  result.push(current.trim().replace(/^"|"$/g, ""));
  return result;
};

const parseCSV = (text) => {
  const raw = String(text).replace(/^\uFEFF/, "").trim();
  const lines = raw.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return { rows: [], headers: [] };
  const firstLine = lines[0];
  const delimiter = firstLine.includes(";") && !firstLine.includes(",") ? ";" : ",";
  const headers = parseCSVLine(lines[0], delimiter);
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i], delimiter);
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = values[idx] != null ? String(values[idx]).trim() : "";
    });
    obj._values = values;
    rows.push(obj);
  }
  return { rows, headers };
};

const formatRupiah = (n) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const pdTahunKey = (idPd, th) => `${idPd}|${th}`;

const BelanjaMaminPdPage = () => {
  const currentYear = new Date().getFullYear();
  const [tahun, setTahun] = useState(String(currentYear));
  const [listDinas, setListDinas] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formPd, setFormPd] = useState("");
  const [formTotal, setFormTotal] = useState("");

  const [csvData, setCsvData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [formatOpen, setFormatOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [checkingCsvDup, setCheckingCsvDup] = useState(false);

  const listTahun = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= 2018; y -= 1) years.push(y);
    return years;
  }, [currentYear]);

  const dinasByName = useMemo(() => {
    const map = new Map();
    listDinas.forEach((d) => {
      const key = String(d.nama_dinas || "").trim().toUpperCase();
      if (key) map.set(key, d);
    });
    return map;
  }, [listDinas]);

  const loadData = useCallback(() => {
    if (!tahun) return;
    setLoading(true);
    axios
      .get(BELANJA_MAMIN_PD_TAHUN + tahun, { timeout: API_TIMEOUT })
      .then((res) => setRows(res.data?.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [tahun]);

  useEffect(() => {
    axios
      .get(DINAS_PERANGKAT_DAERAH, { timeout: API_TIMEOUT })
      .then((res) => setListDinas(res.data?.data || []))
      .catch(() => setListDinas([]));
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const existingPdTahunKeys = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      set.add(pdTahunKey(r.id_perangkat_daerah, r.tahun));
    });
    return set;
  }, [rows]);

  const manualDuplicate = useMemo(() => {
    const idPd = parseInt(formPd, 10);
    const th = parseInt(tahun, 10);
    if (!Number.isFinite(idPd) || !Number.isFinite(th)) return false;
    return existingPdTahunKeys.has(pdTahunKey(idPd, th));
  }, [formPd, tahun, existingPdTahunKeys]);

  const manualDuplicateRow = useMemo(() => {
    const idPd = parseInt(formPd, 10);
    const th = parseInt(tahun, 10);
    if (!Number.isFinite(idPd) || !Number.isFinite(th)) return null;
    return rows.find(
      (r) => r.id_perangkat_daerah === idPd && String(r.tahun) === String(th)
    );
  }, [formPd, tahun, rows]);

  const handleSaveSingle = async () => {
    const idPd = parseInt(formPd, 10);
    const total = parseFloat(String(formTotal).replace(/,/g, "."));
    const th = parseInt(tahun, 10);
    if (!Number.isFinite(idPd) || !Number.isFinite(total) || total < 0) {
      swal("Error", "Pilih perangkat daerah dan isi total belanja dengan benar", "error");
      return;
    }
    if (existingPdTahunKeys.has(pdTahunKey(idPd, th))) {
      const nama = manualDuplicateRow?.nama_dinas || "perangkat daerah ini";
      swal(
        "Duplikat tidak diizinkan",
        `${nama} sudah memiliki data belanja mamin pada tahun ${th}. Hapus data lama di tabel bawah atau pilih PD lain.`,
        "error"
      );
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        BELANJA_MAMIN_PD,
        {
          id_perangkat_daerah: idPd,
          tahun: th,
          total_belanja_mamin: total,
        },
        { timeout: API_TIMEOUT }
      );
      swal("Sukses", "Data belanja berhasil disimpan", "success");
      setFormPd("");
      setFormTotal("");
      loadData();
    } catch (err) {
      swal("Gagal", err.response?.data?.message || err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, nama) => {
    const ok = await swal({
      title: "Hapus data?",
      text: `Hapus belanja mamin untuk ${nama}?`,
      icon: "warning",
      buttons: ["Batal", "Hapus"],
      dangerMode: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${BELANJA_MAMIN_PD}/${id}`, { timeout: API_TIMEOUT });
      swal("Terhapus", "Data berhasil dihapus", "success");
      loadData();
    } catch (err) {
      swal("Gagal", err.response?.data?.message || err.message, "error");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result || "";
      const { rows: parsed, headers } = parseCSV(text);
      const normalized = parsed.map((row) => {
        const out = {};
        const values = row._values || [];
        CSV_COLUMNS.forEach((col, idx) => {
          const byIndex = values[idx] != null ? String(values[idx]).trim() : "";
          const headerName = headers[idx];
          const byHeader = headerName != null ? row[headerName] ?? "" : "";
          const byKey = row[col.key] ?? row[col.label] ?? "";
          const byKeyLower = Object.keys(row).find(
            (k) => k !== "_values" && k.toLowerCase() === col.key.toLowerCase()
          );
          out[col.key] = String(byIndex || byHeader || byKey || (byKeyLower ? row[byKeyLower] : "") || "");
        });
        return out;
      });
      setCsvData(normalized);
      setFileName(file.name);
    };
    reader.readAsText(file, "UTF-8");
    e.target.value = "";
  };

  useEffect(() => {
    if (csvData.length === 0) {
      setCheckingCsvDup(false);
      return;
    }

    const pairs = [];
    const seenInFile = new Map();
    csvData.forEach((row, rowIdx) => {
      const nama = String(row.nama_dinas || "").trim();
      const dinas = nama ? dinasByName.get(nama.toUpperCase()) : null;
      const idPd = dinas?.dinas_id;
      const th = parseInt(String(row.tahun || tahun).trim(), 10);
      if (Number.isFinite(idPd) && Number.isFinite(th)) {
        const key = pdTahunKey(idPd, th);
        if (seenInFile.has(key)) {
          pairs.push({ rowIdx, duplicateInFile: seenInFile.get(key) });
        } else {
          seenInFile.set(key, rowIdx + 1);
          pairs.push({ rowIdx, idPd, th, key });
        }
      }
    });

    const errors = {};
    csvData.forEach((row, rowIdx) => {
      const nama = String(row.nama_dinas || "").trim();
      if (!nama) errors[`${rowIdx}-nama_dinas`] = "Nama PD wajib";
      else if (!dinasByName.has(nama.toUpperCase())) {
        errors[`${rowIdx}-nama_dinas`] = "Perangkat daerah tidak ditemukan";
      }
      const th = String(row.tahun || tahun).trim();
      if (!th || !/^\d{4}$/.test(th)) errors[`${rowIdx}-tahun`] = "Tahun tidak valid";
      const total = String(row.total_belanja_mamin || "").replace(/,/g, ".");
      if (!total || !Number.isFinite(parseFloat(total)) || parseFloat(total) < 0) {
        errors[`${rowIdx}-total`] = "Total tidak valid";
      }
    });

    pairs.forEach((p) => {
      if (p.duplicateInFile != null) {
        errors[`${p.rowIdx}-dup`] =
          `Duplikat dalam file (sama dengan baris ${p.duplicateInFile})`;
      }
    });

    setValidationErrors(errors);

    const checkPairs = pairs
      .filter((p) => p.idPd != null && !errors[`${p.rowIdx}-nama_dinas`])
      .map((p) => ({ id_perangkat_daerah: p.idPd, tahun: p.th }));

    if (checkPairs.length === 0) {
      return;
    }

    setCheckingCsvDup(true);
    axios
      .post(BELANJA_MAMIN_PD_CHECK, { pairs: checkPairs }, { timeout: API_TIMEOUT })
      .then((res) => {
        const exist = new Set();
        (res.data?.data || []).forEach((r) => {
          exist.add(pdTahunKey(r.id_perangkat_daerah, r.tahun));
        });
        setValidationErrors((prev) => {
          const next = { ...prev };
          pairs.forEach((p) => {
            if (p.key && exist.has(p.key)) {
              next[`${p.rowIdx}-dup`] =
                "Sudah ada di database (PD + tahun sama)";
            }
          });
          return next;
        });
      })
      .catch(() => {})
      .finally(() => setCheckingCsvDup(false));
  }, [csvData, dinasByName, tahun]);

  const handleSaveBulk = async () => {
    if (csvData.length === 0) {
      swal("Error", "Tidak ada data CSV", "error");
      return;
    }
    if (Object.keys(validationErrors).length > 0) {
      swal("Error", "Perbaiki baris yang ditandai error", "error");
      return;
    }
    const dupCount = Object.keys(validationErrors).filter((k) =>
      k.endsWith("-dup")
    ).length;
    if (dupCount > 0) {
      swal(
        "Duplikat tidak diizinkan",
        `Terdapat ${dupCount} baris duplikat (dalam file atau sudah ada di database). Perbaiki atau hapus baris tersebut.`,
        "error"
      );
      return;
    }

    const ok = await swal({
      title: "Simpan data CSV?",
      text: `${csvData.length} baris valid akan disimpan. Duplikat PD+tahun akan ditolak.`,
      icon: "info",
      buttons: ["Batal", "Simpan"],
    });
    if (!ok) return;
    setSaving(true);
    const payload = csvData.map((r) => ({
      nama_dinas: r.nama_dinas,
      tahun: r.tahun || tahun,
      total_belanja_mamin: String(r.total_belanja_mamin).replace(/,/g, "."),
    }));
    try {
      const res = await axios.post(
        BELANJA_MAMIN_PD_UPLOAD,
        { data: payload },
        { timeout: API_TIMEOUT }
      );
      const inserted = res.data?.inserted ?? 0;
      const errs = res.data?.errors;
      if (errs?.length) {
        const dupErrs = errs.filter(
          (e) =>
            String(e.msg || "").includes("Duplikat") ||
            String(e.msg || "").includes("Sudah ada")
        );
        swal(
          inserted > 0 ? "Sebagian gagal" : "Duplikat tidak diizinkan",
          inserted > 0
            ? `Berhasil: ${inserted}. Ditolak/ gagal: ${errs.length} baris.`
            : `Semua baris ditolak (${dupErrs.length} duplikat). Periksa file CSV.`,
          inserted > 0 ? "warning" : "error"
        );
      } else {
        swal("Sukses", `Berhasil menyimpan ${inserted} data`, "success");
      }
      setCsvData([]);
      setFileName("");
      loadData();
    } catch (err) {
      swal("Gagal", err.response?.data?.message || err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const rowHasError = (idx) =>
    validationErrors[`${idx}-nama_dinas`] ||
    validationErrors[`${idx}-tahun`] ||
    validationErrors[`${idx}-total`] ||
    validationErrors[`${idx}-dup`];

  return (
    <div className={`animated fadeIn ${s.pageWrap}`}>
      <div className={s.heroSection}>
        <h2>Belanja Mami PD</h2>
        <p>
          Input total belanja makan-minum per perangkat daerah per tahun — satu
          per satu atau unggah CSV. Setiap perangkat daerah hanya boleh satu data
          per tahun (tidak boleh duplikat).
        </p>
      </div>

      <div className="mb-3">
        <Button tag={Link} to="/admin/makan-minum" color="link" className="p-0">
          ← Kembali ke rekap
        </Button>
      </div>

      <div className={s.sectionCard}>
        <div className={s.cardHeader}>Input manual</div>
        <div className={s.cardBody}>
          <div className={s.toolbar}>
            <FormGroup>
              <Label>Tahun anggaran</Label>
              <Input
                type="select"
                value={tahun}
                onChange={(e) => setTahun(e.target.value)}
                style={{ minWidth: 120 }}
              >
                {listTahun.map((y) => (
                  <option key={y} value={String(y)}>
                    {y}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="flex-grow-1" style={{ minWidth: 260 }}>
              <Label>Perangkat daerah</Label>
              <Input
                type="select"
                value={formPd}
                onChange={(e) => setFormPd(e.target.value)}
              >
                <option value="">— Pilih perangkat daerah —</option>
                {listDinas.map((d) => {
                  const sudah = existingPdTahunKeys.has(
                    pdTahunKey(d.dinas_id, parseInt(tahun, 10))
                  );
                  return (
                    <option
                      key={d.dinas_id}
                      value={String(d.dinas_id)}
                      disabled={sudah}
                    >
                      {d.nama_dinas}
                      {sudah ? " (sudah ada)" : ""}
                    </option>
                  );
                })}
              </Input>
              {manualDuplicate && (
                <small className="text-danger d-block mt-1">
                  Perangkat daerah ini sudah memiliki data pada tahun {tahun}.
                  Hapus data lama di tabel bawah jika ingin mengganti.
                </small>
              )}
            </FormGroup>
            <FormGroup>
              <Label>Total belanja (Rp)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formTotal}
                onChange={(e) => setFormTotal(e.target.value)}
                placeholder="0"
                style={{ minWidth: 160 }}
              />
            </FormGroup>
            <FormGroup className="align-self-end">
              <Button
                color="primary"
                onClick={handleSaveSingle}
                disabled={saving || manualDuplicate || !formPd}
              >
                {saving ? <Spinner size="sm" /> : "Simpan"}
              </Button>
            </FormGroup>
          </div>
        </div>
      </div>

      <div className={s.sectionCard}>
        <div
          className={s.cardHeader}
          style={{ cursor: "pointer" }}
          onClick={() => setFormatOpen(!formatOpen)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && setFormatOpen(!formatOpen)}
        >
          Upload CSV
          <i className={`fa fa-chevron-${formatOpen ? "up" : "down"} float-end`} />
        </div>
        <Collapse isOpen={formatOpen}>
          <div className={s.cardBody}>
            <p className="text-muted small">
              Kolom: <strong>nama_dinas</strong>, <strong>tahun</strong>,{" "}
              <strong>total_belanja_mamin</strong>. Jika tahun kosong di CSV,
              digunakan tahun filter di atas ({tahun}). Tidak boleh ada baris
              ganda untuk kombinasi perangkat daerah + tahun yang sama, baik
              dalam file maupun yang sudah tersimpan di database.
            </p>
            <Table size="sm" className={s.formatTable}>
              <thead>
                <tr>
                  {CSV_COLUMNS.map((c) => (
                    <th key={c.key}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Dinas Pendidikan</td>
                  <td>{tahun}</td>
                  <td>150000000</td>
                </tr>
              </tbody>
            </Table>
            <Input type="file" accept=".csv,text/csv" onChange={handleFileChange} />
            {fileName && (
              <p className="small text-muted mt-2 mb-0">
                File: {fileName} ({csvData.length} baris)
              </p>
            )}
            {csvData.length > 0 && (
              <>
                {checkingCsvDup && (
                  <p className="small text-muted mb-2">
                    <Spinner size="sm" /> Memeriksa duplikat…
                  </p>
                )}
                <div className={`${s.tableWrap} mt-3`}>
                  <Table size="sm" responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        {CSV_COLUMNS.map((c) => (
                          <th key={c.key}>{c.label}</th>
                        ))}
                        <th>Keterangan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvData.map((row, idx) => (
                        <tr
                          key={idx}
                          className={rowHasError(idx) ? s.errorCell : ""}
                        >
                          <td>{idx + 1}</td>
                          <td>{row.nama_dinas}</td>
                          <td>{row.tahun || tahun}</td>
                          <td>{row.total_belanja_mamin}</td>
                          <td className="small text-danger">
                            {validationErrors[`${idx}-dup`] ||
                              validationErrors[`${idx}-nama_dinas`] ||
                              validationErrors[`${idx}-tahun`] ||
                              validationErrors[`${idx}-total`] ||
                              ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <Button
                  color="success"
                  className="mt-2"
                  onClick={handleSaveBulk}
                  disabled={
                    saving ||
                    checkingCsvDup ||
                    Object.keys(validationErrors).length > 0
                  }
                >
                  {saving ? <Spinner size="sm" /> : "Simpan CSV ke database"}
                </Button>
              </>
            )}
          </div>
        </Collapse>
      </div>

      <div className={s.sectionCard}>
        <div className={s.cardHeader}>Data tersimpan — tahun {tahun}</div>
        <div className={`${s.cardBody} ${s.tableWrap}`}>
          {loading ? (
            <Spinner color="primary" />
          ) : rows.length === 0 ? (
            <p className="text-muted mb-0">Belum ada data untuk tahun ini.</p>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Perangkat daerah</th>
                  <th className="text-end">Total belanja</th>
                  <th style={{ width: 100 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.id}>
                    <td>{idx + 1}</td>
                    <td>{row.nama_dinas}</td>
                    <td className="text-end">{formatRupiah(row.total_belanja_mamin)}</td>
                    <td>
                      <Button
                        size="sm"
                        color="danger"
                        outline
                        onClick={() => handleDelete(row.id, row.nama_dinas)}
                      >
                        Hapus
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};

export default BelanjaMaminPdPage;
